import { computed, inject, Injectable, signal } from "@angular/core";
import { NotificationService } from "../../../../services/notification/notification.service";
import {
  GenericPortalUserAccountDtoInput,
  GenericUserWorkingLevelDtoInput,
  UpdatePasswordRequestInput,
  User,
  UserAccountDtoInput,
} from "./user.model";
import * as fromGraphql from "./user.graphql";
import { GenericStore } from "../../base/generic.store";

@Injectable({
  providedIn: "root",
})
export class UserStore {
  private notify = inject(NotificationService);
  private genericStore = new GenericStore<User>();

  // Public readonly signals
  // Public readonly signals
  readonly users = this.genericStore.items;
  readonly isQuerying = this.genericStore.isQuerying;
  readonly isMutating = this.genericStore.isMutating;

  // Computed signals

  readonly usersCount = this.genericStore.readItemsCount;

  readonly mappedUsers = computed(() =>
    this.genericStore.mapItems((user) => this.mapUser(user)),
  );

  // the signed-in user, kept apart from the list so loading users never replaces it
  private readonly _me = signal<User | null>(null);
  readonly currentLoggedInUser = this._me.asReadonly();

  readonly getUserInstitutionRole = computed(
    () => this._me()?.institution?.institutionRole,
  );
  readonly isForensicOrganInstitution = computed(
    () => this._me()?.institution?.forensicOrgan,
  );
  readonly getUserInstitutionVoteCode = computed(
    () => this._me()?.institution?.voteCode,
  );
  readonly getUserAuthorities = computed(() => this._me()?.authorities);

  constructor() {
    this.genericStore.onReset(() => this._me.set(null));
  }

  /**
   * Get user by UID
   */

  loadUsers(users: User[]) {
    this.genericStore.loadItems(users);
  }
  getUserByUid(uid: string) {
    return this.mappedUsers().find((user) => user.uid === uid);
  }

  /**
   * Map user for display
   */
  /**
   * Map user for display (can be used by components)
   */
  mapUser(user: User): User | any {
    return {
      ...user,
      roleUidList: user?.roles?.map((x) => x?.uid),
      modRolesListStrings: user?.roles
        ?.map((r) => r?.displayName)
        ?.filter(Boolean)
        ?.join(", "),
      modActive: user?.active ? "YES" : "NO",
      modLocked: user?.accountNonLocked ? "NO" : "YES",
      designationUid: user?.designation?.uid,
      institutionUid: user?.institution?.uid,
      requestDate: user?.createdAt,
    };
  }

  // API Methods Starts Here

  /**
   * Create or update a generic portal user account. The response carries no
   * entity, so the code alone decides success.
   */
  async saveGenericPortalUserAccount(
    genericPortalUserAccountDto: GenericPortalUserAccountDtoInput,
  ): Promise<boolean> {
    const result = await this.genericStore.mutate<User>(
      fromGraphql.SAVE_GENERIC_PORTAL_USER_ACCOUNT,
      {
        variables: { genericPortalUserAccountDto },
        successMessage: "User saved successfully",
        errorMessage: "Failed to save user",
      },
    );

    if (result.success && result.data) {
      this.genericStore.upsertItem(result.data);
      return true;
    } else {
      return false;
    }
  }

  /**
   * Activate or deactivate a generic portal user; the backend flips the
   * account's current state.
   */
  async activateGenericPortalUserAccount(user: User): Promise<boolean> {
    const result = await this.genericStore.mutate<User>(
      fromGraphql.ACTIVATE_GENERIC_PORTAL_USER_ACCOUNT,
      {
        variables: { userUid: user.uid },
        successMessage: false,
        errorMessage: "Failed to Activate/Deactivate user",
      },
    );
    if (!result.success) return false;

    const message = user.active ? "Activated" : "Deactivated";
    if (result.data) this.genericStore.upsertItem(result.data);
    this.notify.success(`User ${message} successfully`);
    return true;
  }

  /**
   * The roles a user holds, from their HAJIS account; the generic user list
   * does not carry them. Null when the account can't be read.
   */
  async getUserRoleUids(
    userUid: string,
  ): Promise<{ fullName?: string; roleUidList: string[] } | null> {
    const result = await this.genericStore.query<User>(
      fromGraphql.GET_USER_ACCOUNT_BY_UID,
      {
        variables: { userUid },
        errorMessage: "Failed to load the user's roles",
      },
    );
    if (!result.success || !result.data) return null;
    return {
      fullName: result.data.fullName ? String(result.data.fullName) : undefined,
      roleUidList: (result.data.roles ?? [])
        .map((role) => role?.uid)
        .filter((uid): uid is string => !!uid),
    };
  }

  /** Replaces the roles a user holds (HAJIS server). */
  async assignRoleToUser(
    userUid: string,
    roleUidList: string[],
  ): Promise<boolean> {
    const result = await this.genericStore.mutate(
      fromGraphql.ASSIGN_ROLE_TO_USER,
      {
        variables: { userUid, roleUidList },
        successMessage: "User roles saved successfully",
        errorMessage: "Failed to save user roles",
      },
    );
    return result.success;
  }

  /**
   * Update a generic portal user's working office level / location.
   * Exposed by the schema as a query rather than a mutation, so it is called
   * with apollo.query and no cache.
   */
  async updateGenericPortalUserWorkingLevel(
    workingOfficeDto: GenericUserWorkingLevelDtoInput,
  ): Promise<boolean> {
    const result = await this.genericStore.query<User>(
      fromGraphql.UPDATE_GENERIC_PORTAL_USER_WORKING_LEVEL,
      {
        variables: { workingOfficeDto },
        fetchPolicy: "no-cache",
        errorPolicy: "all",
        context: { useGeneric: true },
        successMessage: "Working level updated successfully",
        errorMessage: false,
      },
    );

    if (result.success) {
      if (result.data) {
        this.genericStore.upsertItem(result.data);
        if (result.data.uid === this._me()?.uid) this._me.set(result.data);
      }
      return true;
    }

    this.notify.error(result.message || "Failed to update working level");
    return false;
  }

  /**
   * get my Details
   */
  async getMyDetails(): Promise<void> {
    const result = await this.genericStore.query<User>(fromGraphql.MY_DETAILS, {
      errorMessage: "Failed to fetch your details",
    });
    if (result.success) this._me.set(result.data);
  }

  /**
   * Assign user access level
   */
  async changeMyPassword(
    updatePasswordDto: UpdatePasswordRequestInput,
  ): Promise<boolean> {
    const result = await this.genericStore.mutate<User>(
      fromGraphql.CHANGE_PASSWORD,
      {
        variables: { updatePasswordDto },
        successMessage: "Password changed successfully",
        errorMessage: "Failed to change password",
      },
    );

    if (result.success && result.data) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Change password
   */
  async changePassword(changePasswordDto: any): Promise<boolean> {
    const result = await this.genericStore.mutate<boolean>(
      fromGraphql.CHANGE_PASSWORD,
      {
        variables: { changePasswordDto },
        successMessage: "Password changed successfully",
        errorMessage: "Failed to change password",
      },
    );

    if (result.success) {
      return true;
    } else {
      return false;
    }
  }
}
